// Interaction telemetry (spec §6). Builds the event envelope, mirrors events to the
// in-app dev panel, and batches non-blocking POSTs to /api/telemetry. NEVER blocks the
// UI: failures are swallowed; on unload we best-effort sendBeacon the tail.

export interface TelemetryContext {
  condition: string;
  join_gva_id: number;
  target_outlet: string;
}

export interface TelemetryEvent {
  session_id: string;
  participant_id: string;
  condition: string;
  join_gva_id: number;
  target_outlet: string;
  ts: number;
  event: string;
  payload: Record<string, unknown>;
}

function rid(prefix: string): string {
  return prefix + Math.random().toString(36).slice(2, 9);
}

const ENDPOINT = "/api/telemetry";
const FLUSH_MS = 1500;
const MAX_BUFFER = 400;

export class Telemetry {
  session_id = rid("sess_");
  /** Pre-consent placeholder; App swaps in the consent record's pseudonymous code
   *  once the participant agrees (consent.ts), so only the consent decision itself
   *  is ever logged as anonymous. */
  participant_id = rid("anon_");
  events: TelemetryEvent[] = [];
  onChange: (() => void) | null = null;
  /** Consent switch: false → log() is a no-op (set on withdrawal, spec §7). */
  enabled = true;

  private getCtx: () => TelemetryContext;
  private pending: TelemetryEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(getCtx: () => TelemetryContext) {
    this.getCtx = getCtx;
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => this.flush(true));
    }
  }

  log(event: string, payload: Record<string, unknown> = {}) {
    if (!this.enabled) return;
    const ctx = this.getCtx();
    const ev: TelemetryEvent = {
      session_id: this.session_id, participant_id: this.participant_id,
      condition: ctx.condition, join_gva_id: ctx.join_gva_id, target_outlet: ctx.target_outlet,
      ts: Date.now(), event, payload,
    };
    this.events.push(ev);
    if (this.events.length > MAX_BUFFER) this.events = this.events.slice(-MAX_BUFFER);
    this.pending.push(ev);
    this.onChange?.();
    this.schedule();
  }

  clear() {
    this.events = [];
    this.onChange?.();
  }

  /** Withdrawal: send whatever is buffered (incl. the withdrawal event itself), then
   *  go silent — every later log() is dropped until consent is given again. */
  stop() {
    this.flush();
    this.enabled = false;
    this.events = [];
    this.onChange?.();
  }

  private schedule() {
    if (this.timer) return;
    this.timer = setTimeout(() => this.flush(), FLUSH_MS);
  }

  private flush(sync = false) {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    if (!this.pending.length) return;
    const batch = this.pending;
    this.pending = [];
    const body = JSON.stringify({ events: batch });
    try {
      if (sync && navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
      } else {
        fetch(ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true })
          .catch(() => { /* swallow — telemetry never blocks or surfaces errors */ });
      }
    } catch { /* swallow */ }
  }
}
